import { useMemo } from 'react'

import { MarketplaceDbIntegration } from 'generated/resources/datatable.graphql'
import { ReadSyncValidationsQuery } from 'generated/resources/graphql'

export type SyncIssue = {
  title: string
  description: string
  buttonLabel?: string
  buttonUrl?: string
  onAction?: () => void
  affectedNames?: string[]
}

export type IssuesByKeyParams = {
  integrationName?: string
  datoId?: string | null
  companyId?: string
}

export type IssuesByKeyFactory = (params: IssuesByKeyParams) => Record<string, SyncIssue>

type SyncValidationResult = NonNullable<
  ReadSyncValidationsQuery['integrations']['syncValidationsConnection']['nodes'][0]
>['issues'][0]

const resolveIssues = (
  validationResults: SyncValidationResult[],
  issuesByKey: Record<string, SyncIssue>,
  marketplaceIntegration: MarketplaceDbIntegration | null
): SyncIssue[] => {
  const knownIssues: SyncIssue[] = []
  const unknownKeys: string[] = []

  for (const result of validationResults) {
    const issue = issuesByKey[result.errorMessage.key]

    if (issue) {
      knownIssues.push({
        title: issue.title,
        description: issue.description,
        buttonLabel: issue.buttonLabel,
        buttonUrl: issue.buttonUrl,
        onAction: issue.onAction,
        affectedNames: result.affectedNames,
      })
    } else {
      unknownKeys.push(result.errorMessage.key)
    }
  }

  if (unknownKeys.length > 0) {
    throw new Error(
      `Unknown sync validation keys: ${unknownKeys.join(', ')} (integration: ${marketplaceIntegration?.name ?? 'unknown'})`
    )
  }

  return knownIssues
}

export const useSyncIssues = (
  validationResults: SyncValidationResult[],
  marketplaceIntegration: MarketplaceDbIntegration | null,
  issuesByKeyFactory: IssuesByKeyFactory,
  companyId?: string
): SyncIssue[] => {
  const issuesByKey = issuesByKeyFactory({
    integrationName: marketplaceIntegration?.displayName,
    datoId: marketplaceIntegration?.datoId,
    companyId,
  })

  return resolveIssues(validationResults, issuesByKey, marketplaceIntegration)
}

export const useIssuesWithModalClose = (issues: SyncIssue[], closeModal: () => void) => {
  return useMemo(
    () =>
      issues.map((issue) => {
        if (!issue.onAction) return issue
        const { onAction } = issue
        return {
          ...issue,
          onAction: () => {
            onAction()
            closeModal()
          },
        }
      }),
    [issues, closeModal]
  )
}
